import type { OrderEvent } from '@/services/orders';

// Status flow
export const WORKFLOW_STEPS = [
  'paid', 'queued', 'printing', 'post_processing', 'packed', 'shipped', 'delivered',
] as const;

export const LEGACY_WORKFLOW = [
  'received', 'confirmed', 'printing', 'shipped', 'delivered',
] as const;

export const STATUS_TRANSITIONS: Record<string, string> = {
  paid: 'queued',
  queued: 'printing',
  printing: 'post_processing',
  post_processing: 'packed',
  packed: 'shipped',
  shipped: 'delivered',
  received: 'confirmed',
  confirmed: 'printing',
};

export const ADVANCE_LABELS: Record<string, string> = {
  paid: 'Move to queue',
  queued: 'Start printing',
  printing: 'Begin post-processing',
  post_processing: 'Mark as packed',
  packed: 'Mark as shipped',
  shipped: 'Mark as delivered',
  received: 'Confirm Order',
  confirmed: 'Start printing',
};

export const STEP_LABELS: Record<string, string> = {
  paid: 'Payment received',
  queued: 'Queued for printing',
  printing: 'Printing',
  post_processing: 'Post-processing',
  packed: 'Packed',
  shipped: 'Shipped',
  delivered: 'Delivered',
  received: 'Received',
  confirmed: 'Confirmed',
};

export const TERMINAL_STATUSES = new Set([
  'cancelled', 'rejected', 'delivered', 'refunded', 'failed',
]);

export const FAILABLE_STATUSES = new Set(['printing', 'post_processing']);

// Tab system
export type TabId = 'job' | 'fulfill' | 'finance' | 'admin' | 'status';

export const TABS: readonly { readonly id: TabId; readonly label: string; readonly mobileOnly?: boolean }[] = [
  { id: 'job', label: 'Job & production' },
  { id: 'fulfill', label: 'Fulfillment' },
  { id: 'finance', label: 'Financials' },
  { id: 'admin', label: 'Admin' },
  { id: 'status', label: 'Status', mobileOnly: true },
];

export const DEFAULT_TAB_BY_STATUS: Record<string, TabId> = {
  paid: 'job',
  queued: 'job',
  printing: 'job',
  post_processing: 'job',
  packed: 'fulfill',
  shipped: 'fulfill',
  delivered: 'finance',
  cancelled: 'admin',
  rejected: 'admin',
  failed: 'job',
  refunded: 'finance',
  received: 'job',
  confirmed: 'job',
};

// Dropdowns
export const CARRIERS = [
  { value: 'usps', label: 'USPS' },
  { value: 'ups', label: 'UPS' },
  { value: 'fedex', label: 'FedEx' },
  { value: 'dhl', label: 'DHL' },
  { value: 'other', label: 'Other' },
] as const;

export const CARRIER_SERVICES: Record<string, readonly string[]> = {
  usps: ['Priority Mail', 'Priority Mail Express', 'First Class', 'Ground Advantage'],
  ups: ['Ground', 'Next Day Air', '2nd Day Air', '3 Day Select'],
  fedex: ['Ground', 'Express Saver', '2Day', 'Overnight'],
  dhl: ['Express', 'Express Worldwide', 'Economy Select'],
  other: ['Standard'],
};

export const CANCEL_REASONS = [
  'Customer requested',
  'Unprintable file',
  'Out of stock',
  'Payment issue',
  'Other',
] as const;

export const REJECT_REASONS = [
  'Policy violation',
  'Copyright/IP issue',
  'Safety concern',
  'Fraudulent order',
  'Unprintable file',
  'Other',
] as const;

export const FAILURE_CATEGORIES = [
  'Adhesion failure',
  'Layer shift',
  'Spaghetti / detachment',
  'Stringing / quality',
  'Machine error',
  'Filament issue',
  'Other',
] as const;

// Utility
export const detectWorkflow = (events: readonly OrderEvent[]): readonly string[] => {
  const statuses = new Set(events.map((e) => e.status));
  if (statuses.has('paid') || statuses.has('queued') || statuses.has('post_processing') || statuses.has('packed')) {
    return [...WORKFLOW_STEPS];
  }
  return [...LEGACY_WORKFLOW];
};

export const formatMinutes = (mins: number): string => {
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
};

export const formatCurrency = (amount: number): string =>
  `$${Math.abs(amount).toFixed(2)}`;

export const isSameAddress = (
  a: { readonly line1: string; readonly city: string; readonly postalCode: string } | null,
  b: { readonly line1: string; readonly city: string; readonly postalCode: string } | null,
): boolean => {
  if (!a || !b) return false;
  return a.line1 === b.line1 && a.city === b.city && a.postalCode === b.postalCode;
};
